import { useEffect, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { useNavigate } from "react-router-dom";

import ListerSearch from "../../components/Lister/ListerSearch/ListerSearch";
import LiveLister from "../../components/Lister/LiveLister";
import MultiFilterDropdown from "../../components/MultiFilterDropdown/MultiFilterDropdown";
import { useSession } from "../../contexts/SessionContext";
import { PermissionComponent } from "../../services/PermissionGuard";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import { fetchFlags, generateRemoteUrl, languageOptions } from "./Users.function";

export const Users = () => {
  const { language } = useSession();
  const navigate = useNavigate();
  const [selectedFlagFilters, setSelectedFlagFilters] = useState<any[]>([]);
  const [selectedLanguageFilters, setSelectedLanguageFilters] = useState<any[]>([]);
  const [uniqueFlags, setUniqueFlags] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingFlags, setLoadingFlags] = useState(true);

  const columns = [
    { header: translate(language, "PAGES.USERS.ID"), key: "id", sortable: true },
    { header: translate(language, "PAGES.USERS.FIRST_NAME"), key: "first_name", sortable: true },
    { header: translate(language, "PAGES.USERS.LAST_NAME"), key: "last_name", sortable: true },
    { header: translate(language, "PAGES.USERS.EMAIL"), key: "email", sortable: true },
    {
      header: translate(language, "PAGES.USERS.ACTIVE"),
      key: "is_active",
      render: (user: any) => (
        <div
          title={`${formatDateHumanly(language, user.dt_start)} - ${formatDateHumanly(language, user.dt_end)}`}
          className="m-regular"
        >
          {user.is_active ? (
            <span className="dst-icon-check green-font" />
          ) : (
            <span className="dst-icon-x dark-red-font" />
          )}
        </div>
      ),
    },
    { header: translate(language, "PAGES.USERS.LANGUAGE"), key: "language", sortable: true },
  ];

  useEffect(() => {
    fetchFlags(language, setUniqueFlags, setLoadingFlags);
    // eslint-disable-next-line
  }, []);

  return (
    <div className="m-5 position-relative">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
        <div className="d-flex align-items-center gap-3">
          <h2 className="mb-0">{translate(language, "PAGES.USERS.TITLE")}</h2>
          <ListerSearch setSearchQuery={setSearchQuery} timeDelay={500} showSearchIcon />
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <div>
            <MultiFilterDropdown
              tags={languageOptions(language)}
              onSelectTags={(languages) => setSelectedLanguageFilters(languages)}
              language={language}
              inputTitle={translate(language, "PAGES.USERS.FILTERS.LANGUAGE")}
              inputLabel={translate(language, "PAGES.USERS.FILTERS.INPUT_LABEL")}
              inputPlaceholder={translate(language, "PAGES.USERS.FILTERS.INPUT_PLACEHOLDER")}
              showSearchBar
            />
          </div>
          <PermissionComponent neededPermission={[{ methods: ["GET"], route: "/flags" }]}>
            <MultiFilterDropdown
              tags={uniqueFlags.map((flag: any) => ({
                display: flag.name,
                value: flag.id,
              }))}
              onSelectTags={(flag) => setSelectedFlagFilters(flag)}
              language={language}
              inputTitle={translate(language, "PAGES.USERS.MULTI_FILTER_DROPDOWN.TITLE")}
              inputLabel={translate(language, "PAGES.USERS.MULTI_FILTER_DROPDOWN.LABEL")}
              inputPlaceholder={translate(language, "PAGES.USERS.MULTI_FILTER_DROPDOWN.PLACEHOLDER")}
              showSearchBar
              mock={loadingFlags}
            />
          </PermissionComponent>
        </div>
      </div>
      <LiveLister
        remote={generateRemoteUrl(searchQuery, selectedFlagFilters, selectedLanguageFilters)}
        remoteKey="users"
        remoteTransform={(user: any) => ({
          id: user.id,
          first_name: user.first_name,
          last_name: user.last_name,
          dt_start: user.dt_start,
          dt_end: user.dt_end,
          is_active: user.is_active,
          email: user.email,
          language: (
            <ReactCountryFlag
              svg
              countryCode={user.language.split("-")[1]}
              title={user.language}
              className="country-flag"
            />
          ),
          flags: user.flags.map((flag: any) => flag.name).join(", "),
        })}
        columns={columns}
        additionalButton={(item: any) => (
          <span
            className="clickable xs-regular"
            onClick={(event) => completeNavigate(event, navigate, `/user/${item.id}`)}
          >
            <span className="dst-icon-arrow-narrow-right" style={{ fontSize: "15px" }} />
          </span>
        )}
        showHeader
        pagination
        itemsPerPage={20}
      />
    </div>
  );
};

export default Users;
